import {
  JORNADA_ITEM_TIPOS,
} from '../constants/jornadaItemTipo';

/** Origem mobile: algum item tem captura ou coordenadas. */
export const isJornadaOrigemMobile = (itens = []) =>
  (itens || []).some(
    (item) => item?.capturadoEm
      || item?.latitude
      || item?.longitude,
  );

export const itensByTipo = (itens = []) =>
  (itens || []).reduce((acc, item) => {
    if (item?.tipo) acc[item.tipo] = item;
    return acc;
  }, {});

export const timesFromItens = (itens = []) => {
  const byTipo = itensByTipo(itens);
  return JORNADA_ITEM_TIPOS.reduce((acc, { tipo, timeField }) => {
    acc[timeField] = byTipo[tipo]?.horario || null;
    return acc;
  }, {});
};

/** Monta itens só com tipo + horário (create web / edit web). */
export const buildItensFromTimes = (values, existingItens = []) => {
  const byTipo = itensByTipo(existingItens);
  return JORNADA_ITEM_TIPOS
    .map(({ tipo, timeField }) => {
      const horario = values[timeField];
      if (!horario) return null;
      const prev = byTipo[tipo];
      return {
        ...(prev?.id ? { id: prev.id } : {}),
        tipo,
        horario,
      };
    })
    .filter(Boolean);
};

export const formatEnderecoItem = (enderecoJson) => {
  if (!enderecoJson) return null;
  let data = enderecoJson;
  if (typeof enderecoJson === 'string') {
    try {
      data = JSON.parse(enderecoJson);
    } catch (e) {
      return enderecoJson;
    }
  }
  if (typeof data !== 'object' || data == null) return null;
  const parts = [
    data.rua || data.road || data.logradouro,
    data.numero || data.number,
    data.bairro || data.suburb || data.neighbourhood,
    data.cidade || data.city || data.localidade,
    data.estado || data.state || data.uf,
  ].filter(Boolean);
  if (parts.length) return parts.join(', ');
  if (data.formatted || data.display_name || data.endereco) {
    return data.formatted || data.display_name || data.endereco;
  }
  return null;
};

export const mapsUrlFromItem = (item) => {
  if (!item) return null;
  if (item.latitude && item.longitude) {
    return `https://www.google.com/maps?q=${encodeURIComponent(`${item.latitude},${item.longitude}`)}`;
  }
  const endereco = formatEnderecoItem(item.enderecoJson);
  if (endereco) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(endereco)}`;
  }
  return null;
};

const coordKey = (item) => `${item.latitude},${item.longitude}`;

/** Maps com todos os pontos do dia (ordem dos tipos). Deduplica coords consecutivas. */
export const mapsUrlFromItens = (itens = []) => {
  const byTipo = itensByTipo(itens);
  const points = [];
  JORNADA_ITEM_TIPOS.forEach(({ tipo }) => {
    const item = byTipo[tipo];
    if (!item?.latitude || !item?.longitude) return;
    const key = coordKey(item);
    if (points.length && points[points.length - 1].key === key) return;
    points.push({ key, lat: item.latitude, lng: item.longitude });
  });
  if (!points.length) return null;
  if (points.length === 1) {
    return `https://www.google.com/maps?q=${encodeURIComponent(`${points[0].lat},${points[0].lng}`)}`;
  }
  const path = points.map((p) => `${p.lat},${p.lng}`).join('/');
  return `https://www.google.com/maps/dir/${path}`;
};

const GEO_POINT_OFFSET_STEP = 0.00008;

/** Pontos com coords para Leaflet (um por tipo; offset se mesma coord). */
export const geoPointsFromItens = (itens = []) => {
  const byTipo = itensByTipo(itens);
  const raw = [];
  JORNADA_ITEM_TIPOS.forEach(({ tipo, label }) => {
    const item = byTipo[tipo];
    if (!item?.latitude || !item?.longitude) return;
    const lat = parseFloat(item.latitude);
    const lng = parseFloat(item.longitude);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    raw.push({
      tipo,
      title: label,
      horario: item.horario || null,
      endereco: formatEnderecoItem(item.enderecoJson),
      lat,
      lng,
    });
  });

  const coordCounts = {};
  return raw.map((point) => {
    const key = `${point.lat},${point.lng}`;
    const index = coordCounts[key] || 0;
    coordCounts[key] = index + 1;
    if (index === 0) return point;
    const angle = (index - 1) * (Math.PI / 2);
    const factor = index * GEO_POINT_OFFSET_STEP;
    return {
      ...point,
      lat: point.lat + Math.sin(angle) * factor,
      lng: point.lng + Math.cos(angle) * factor,
    };
  });
};

/** Linha do banner de horários (todos os tipos, com ou sem geo). */
export const horariosBannerFromItens = (itens = []) => {
  const byTipo = itensByTipo(itens);
  return JORNADA_ITEM_TIPOS.map(({ tipo, label }) => {
    const item = byTipo[tipo];
    return {
      tipo,
      label,
      horario: item?.horario || null,
      hasGeo: Boolean(item?.latitude && item?.longitude),
    };
  });
};

export const getInspetorId = (inspetor) => {
  if (!inspetor) return null;
  if (typeof inspetor === 'object') return inspetor.id || null;
  return inspetor;
};

export const canEditJornada = (row, authId, adminPermission) => {
  if (adminPermission) return true;
  if (!authId || !row) return false;
  if (getInspetorId(row.inspetor) === authId) return true;
  return Boolean(row.canEdit);
};

export const computeJornadaTotalMinutes = ({
  inicioJornada,
  inicioIntervalo,
  finalIntervalo,
  finalJornada,
}, convertTimeToMinutes) => {
  const inicioJornadaMinutes = convertTimeToMinutes(inicioJornada);
  const inicioIntervaloMinutes = convertTimeToMinutes(inicioIntervalo);
  const finalIntervaloMinutes = convertTimeToMinutes(finalIntervalo);
  const finalJornadaMinutes = convertTimeToMinutes(finalJornada);

  const first = (inicioJornadaMinutes && inicioIntervaloMinutes
    && inicioIntervaloMinutes > inicioJornadaMinutes)
    ? inicioIntervaloMinutes - inicioJornadaMinutes
    : null;
  const second = (finalJornadaMinutes && finalIntervaloMinutes
    && finalJornadaMinutes > finalIntervaloMinutes)
    ? finalJornadaMinutes - finalIntervaloMinutes
    : null;

  if (first === null && second === null) return null;
  return (first || 0) + (second || 0);
};

export const JORNADA_FORM_ID_NOVO = 'novo';

export const readJornadaFormId = (search) => {
  const id = new URLSearchParams(search).get('id');
  if (!id) return { open: false, formId: null };
  if (id === JORNADA_FORM_ID_NOVO) return { open: true, formId: null };
  return { open: true, formId: id };
};

export const jornadaListUrl = (path, search, formId) => {
  const next = new URLSearchParams(search);
  if (formId) next.set('id', formId);
  else next.delete('id');
  const qs = next.toString();
  return qs ? `${path}?${qs}` : path;
};
